import * as signalR from '@microsoft/signalr';
import { env } from '@/lib/env';

// ─────────────────────────────────────────────────────────────────────────────
// AIProcessHub — singleton SignalR client for ai-process workflow events
// ─────────────────────────────────────────────────────────────────────────────
//
// COOKIE-CUTTER REALTIME PRIMITIVE
//   Pairs with the [CodeFirstWorkflow]-decorated workflows on the backend.
//   Any feature that triggers an AIProcess workflow (Review with AI,
//   Analyze Lab Report, future workflows) gets live progress messages
//   for free by subscribing here — no per-feature hub plumbing needed.
//
// HOW IT MAPS TO THE BACKEND
//   • Hub URL: <SIGNALR_URL>/aiprocesshub
//   • Hub method invoked from client: Watch(userType, userId)
//     — registers this connection to receive process events scoped to
//     the current user. Tenant scoping happens server-side via the
//     SignalR groups the hub uses internally.
//   • Server pushes:
//       - 'added'   : (process: AIProcess) — a new run started
//       - 'updated' : (process: AIProcess) — status change OR new log entry
//
// LIFECYCLE
//   The hub is a process-wide singleton — one connection per browser tab,
//   shared across every feature that subscribes. The hook
//   (useWorkflowProgress) calls ensureStarted() on mount and adds /
//   removes its handler on a per-component basis. Disposing the last
//   subscriber does NOT close the connection — keeping it alive is
//   cheap and avoids reconnect storms when drawers open/close.
//
// AUTH
//   The connection picks up the JWT cookie sent with the negotiate
//   request. The backend's hub auth middleware uses the same identity
//   the API does. No bespoke token plumbing needed.
//
// REUSE PATTERN
//   import { aiProcessHub } from '@/realtime/AIProcessHub';
//   await aiProcessHub.ensureStarted();
//   await aiProcessHub.watch(userType, userId);
//   const off = aiProcessHub.onProcessUpdated((p) => { ... });
//   // … later
//   off();   // remove this handler only
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Shape the backend pushes via 'added' / 'updated' events. Mirrors
 * <c>OO.Domain.AIProcessInfo.AIProcess</c> with only the fields the
 * frontend needs at the realtime path. Full envelope is fetched via
 * the REST API when the workflow finishes.
 */
export interface AIProcessRealtime {
  /** Opaque key — matches <c>StartAIProcessRequest.Key</c>. */
  key: string;
  /** Display title set when the workflow was triggered. */
  title?: string | null;
  /** "QUEUED" | "RUNNING" | "AWAITING_INPUT" | "COMPLETED" | "FAILED". */
  status?: string;
  /** Per-step log entries. Each entry has stepId + description; the most recent log line is the "currently active" status. */
  logs?: Array<{
    stepId?: string | null;
    log?: string | null;
    date?: string;
  }>;
  /** Surfaced result payload(s). Filled by AddLog calls + Step 4 SignalR notify. */
  result?: unknown;
}

type Handler = (process: AIProcessRealtime) => void;

class AIProcessHubImpl {
  private connection: signalR.HubConnection | null = null;
  private startPromise: Promise<void> | null = null;
  private addedHandlers = new Set<Handler>();
  private updatedHandlers = new Set<Handler>();
  private watchedUserType: number | null = null;
  private watchedUserId: number | null = null;

  private ensureConnection(): signalR.HubConnection {
    if (this.connection) return this.connection;
    if (!env.signalRUrl) {
      throw new Error(
        'SIGNALR_URL is not configured. Set VITE_SIGNALR_URL in .env.<mode>.'
      );
    }
    const url = `${env.signalRUrl.replace(/\/$/, '')}/aiprocesshub`;
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(url, { withCredentials: true })
      .configureLogging(signalR.LogLevel.Warning)
      .withAutomaticReconnect()
      .build();

    this.connection.on('added', (process: AIProcessRealtime) => {
      for (const h of this.addedHandlers) h(process);
    });
    this.connection.on('updated', (process: AIProcessRealtime) => {
      for (const h of this.updatedHandlers) h(process);
    });
    this.connection.onreconnected(() => {
      // After a reconnect we must re-subscribe to the user's process feed.
      if (this.watchedUserType != null && this.watchedUserId != null) {
        void this.connection?.invoke('Watch', this.watchedUserType, this.watchedUserId);
      }
    });
    return this.connection;
  }

  /**
   * Idempotent — safe to call from multiple components on mount. Returns
   * the same Promise while a start is in flight so concurrent callers
   * don't trigger duplicate negotiate requests.
   */
  async ensureStarted(): Promise<void> {
    const connection = this.ensureConnection();
    if (connection.state === signalR.HubConnectionState.Connected) return;
    if (this.startPromise) return this.startPromise;
    this.startPromise = (async () => {
      try {
        await connection.start();
      } catch (e) {
        // Don't crash the app — features fall back to polling. Log loudly.
        // eslint-disable-next-line no-console
        console.warn('[AIProcessHub] start failed; features will fall back to polling.', e);
      } finally {
        this.startPromise = null;
      }
    })();
    return this.startPromise;
  }

  /**
   * Tell the hub which user-scoped feed this connection wants. Matches the
   * provider app's call: Watch(ConversationUserType userType, long userId).
   * The hub adds this connection to the group "AI-Process-{userType}-{userId}".
   *
   * <paramref name="userType"/> values mirror C# ConversationUserType:
   *   1 = Member, 2 = Merchant, 3 = Bot, 4 = FamilyContact, 5 = System.
   * Agent Studio operators are TenantManagers, scoped as Merchant (2).
   */
  async watch(userType: number, userId: number): Promise<void> {
    if (!this.connection) await this.ensureStarted();
    if (!this.connection) return;
    if (this.connection.state !== signalR.HubConnectionState.Connected) return;
    this.watchedUserType = userType;
    this.watchedUserId = userId;
    try {
      await this.connection.invoke('Watch', userType, userId);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[AIProcessHub] Watch invocation failed.', e);
    }
  }

  /** Subscribe to 'added' events. Returns an unsubscribe function. */
  onProcessAdded(handler: Handler): () => void {
    this.addedHandlers.add(handler);
    return () => this.addedHandlers.delete(handler);
  }

  /** Subscribe to 'updated' events. Returns an unsubscribe function. */
  onProcessUpdated(handler: Handler): () => void {
    this.updatedHandlers.add(handler);
    return () => this.updatedHandlers.delete(handler);
  }

  /**
   * True when the connection is alive. Components fall back to polling
   * when this is false (e.g., during initial connect, or if SignalR is
   * unreachable).
   */
  get isConnected(): boolean {
    return this.connection?.state === signalR.HubConnectionState.Connected;
  }
}

/**
 * Process-wide singleton. Import directly — there's no React context
 * because the hub is the same for every consumer in a given browser tab.
 */
export const aiProcessHub = new AIProcessHubImpl();
